import React, { useEffect, useRef, useState } from 'react';
import { Pagination, Table } from '@ver-uds/react';
import type { TableInstance } from 'react-table';
import { HiringSite, State } from 'eve-account-client';
import { Accordion } from '@ver-uds/uswds-react';
import { useAuthState } from '@vis-auth/react';
import { useParams } from 'react-router-dom';
import { useHiringSitesPaginatedQuery } from '../../../services';
import { columns, rowActions } from './common';
import GroupAccordionHeader from './GroupAccordionHeader';
import OpenModalType from '../OpenModalType';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

export type EditAccordionProps = {
  hiringSiteState: State;
  onModalOpen: (hiringSite: HiringSite, modalType: OpenModalType) => void;
};

const EditAccordion = ({ hiringSiteState, onModalOpen }: EditAccordionProps): JSX.Element => {
  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  let accountId = user?.account_id as string;

  const { subAccountId } = useParams<{ subAccountId?: string }>();
  if (subAccountId) {
    accountId = subAccountId;
  }

  const gotoPageRef = useRef<TableInstance['gotoPage']>(() => {
    // no-op
  });

  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(DEFAULT_PAGE_SIZE_OPTIONS[0]);
  const { isLoading, isFetching, resolvedData } = useHiringSitesPaginatedQuery(
    accountId,
    tablePageIndex,
    tablePageSize,
    hiringSiteState.code,
  );
  const hiringSites = resolvedData?.paginated?.data || [];
  const siteCount = resolvedData?.paginated?.totalElements || 0;
  const tablePageCount = Math.ceil(siteCount / tablePageSize);

  useEffect(() => {
    // If we've just deleted the last hiring site on a page,
    // our current pageIndex (0-based) will equal the number of pages (1-based).
    //
    // This means we've overflowed the page count options, so we go back one page.
    if (!isFetching && tablePageIndex > 0 && tablePageIndex >= tablePageCount) {
      gotoPageRef.current(tablePageIndex - 1);
    }
  }, [tablePageIndex, tablePageCount, isFetching]);

  if (siteCount === 0) {
    return <></>;
  }

  return (
    <>
      <GroupAccordionHeader siteCount={siteCount} hiringSiteState={hiringSiteState} />
      <Accordion.Content id={hiringSiteState.code}>
        <Table
          id={`${hiringSiteState.code}-hiring-sites-table`}
          columns={columns}
          data={hiringSites}
          variant="default"
          initialState={{
            pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0],
          }}
          manualSort
          pagination={{
            render: (
              dataSize,
              {
                pageCount,
                pageOptions,
                gotoPage,
                previousPage,
                nextPage,
                setPageSize,
                canPreviousPage,
                canNextPage,
                pageIndex,
                pageSize,
              },
            ) => {
              gotoPageRef.current = gotoPage;
              return (
                <Pagination
                  dataSize={dataSize}
                  pageCount={pageCount}
                  pageOptions={pageOptions}
                  gotoPage={gotoPage}
                  previousPage={previousPage}
                  nextPage={nextPage}
                  setPageSize={setPageSize}
                  canPreviousPage={canPreviousPage}
                  canNextPage={canNextPage}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
                />
              );
            },
            manual: {
              onPageChange: ({ pageIndex, pageSize }) => {
                setTablePageIndex(pageIndex);
                setTablePageSize(pageSize);
              },
              rowCount: siteCount,
              pageCount: tablePageCount,
            },
            isLoading: isLoading || isFetching,
          }}
          rowActions={rowActions(onModalOpen)}
        />
      </Accordion.Content>
    </>
  );
};
export default EditAccordion;
